import React from 'react';
import { CreditCard, Calendar, Gift, AlertTriangle, CheckCircle } from 'lucide-react';
import Card from '../ui/Card';

const SubscriptionStatus = ({ subscription, subscriptionLoading }) => {
  if (subscriptionLoading) {
    return (
      <Card>
        <Card.Content>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: 'var(--color-text-muted)'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid var(--color-border)',
              borderTop: '2px solid var(--color-accent)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ marginLeft: '12px', fontSize: '14px' }}>Loading subscription...</span>
          </div>
        </Card.Content>
      </Card>
    );
  }

  if (!subscription) {
    return (
      <Card style={{ border: '1px solid var(--color-danger-border)', backgroundColor: 'var(--color-danger-bg)' }}>
        <Card.Content>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: 'var(--color-danger-hover)'
          }}>
            <AlertTriangle size={20} />
            <div>
              <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                No Active Subscription
              </p>
              <p style={{ margin: 0, fontSize: '12px', marginTop: '4px' }}>
                Contact support to activate your account
              </p>
            </div>
          </div>
        </Card.Content>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getExpiryDisplay = (endDate) => {
    if (!endDate) return { text: 'No expiry date', isExpired: false };
    
    try {
      const expiryDate = new Date(endDate);
      const today = new Date();
      
      // Remove time component for accurate date comparison
      const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      const formattedDate = formatDate(endDate);
      
      if (expiryDateOnly < todayOnly) {
        return { text: `Expired on ${formattedDate}`, isExpired: true };
      } else if (expiryDateOnly.getTime() === todayOnly.getTime()) {
        return { text: `Expires today`, isExpired: false };
      } else {
        return { text: `Expires: ${formattedDate}`, isExpired: false };
      }
    } catch {
      return { text: 'Invalid expiry date', isExpired: false };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return { bg: 'var(--color-success-bg)', text: 'var(--color-success-fg)', border: 'var(--color-success-border)', icon: CheckCircle };
      case 'expired':
        return { bg: 'var(--color-danger-bg)', text: 'var(--color-danger-hover)', border: 'var(--color-danger-border)', icon: AlertTriangle };
      default:
        return { bg: 'var(--color-bg-subtle)', text: 'var(--color-text-secondary)', border: 'var(--color-border-strong)', icon: AlertTriangle };
    }
  };

  const getPlanIcon = (planName) => {
    if (planName?.toLowerCase().includes('trial')) {
      return Gift;
    }
    return CreditCard;
  };

  const statusColors = getStatusColor(subscription.status);
  const PlanIcon = getPlanIcon(subscription.plan_name);
  const StatusIcon = statusColors.icon;

  return (
    <Card style={{ 
      background: `linear-gradient(135deg, ${statusColors.bg} 0%, white 100%)`,
      border: `1px solid ${statusColors.border}`
    }}>
      <Card.Content>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <PlanIcon size={20} color={statusColors.text} />
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: 0
            }}>
              {subscription.plan_name}
            </h3>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`
          }}>
            <StatusIcon size={12} />
            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <p style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              margin: 0,
              marginBottom: '4px'
            }}>
              Credits Remaining
            </p>
            <p style={{
              fontSize: '20px',
              fontWeight: '700',
              color: subscription.credits_remaining > 0 ? 'var(--color-success)' : 'var(--color-danger)',
              margin: 0
            }}>
              {subscription.credits_remaining?.toLocaleString() || 0}
            </p>
            <p style={{
              fontSize: '10px',
              color: 'var(--color-text-disabled)',
              margin: 0
            }}>
              of {subscription.credits_per_month?.toLocaleString() || 0} monthly
            </p>
          </div>
          
          <div>
            <p style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              margin: 0,
              marginBottom: '4px'
            }}>
              Credits Used
            </p>
            <p style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--color-text-muted)',
              margin: 0
            }}>
              {subscription.total_credits_used?.toLocaleString() || 0}
            </p>
            <p style={{
              fontSize: '10px',
              color: 'var(--color-text-disabled)',
              margin: 0
            }}>
              total lifetime usage
            </p>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '12px',
          borderTop: '1px solid var(--color-border)',
          fontSize: '12px',
          color: 'var(--color-text-muted)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <Calendar size={12} />
            <span style={{
              color: getExpiryDisplay(subscription.end_date).isExpired ? 'var(--color-danger-hover)' : 'var(--color-text-muted)',
              fontWeight: getExpiryDisplay(subscription.end_date).isExpired ? '500' : 'normal'
            }}>
              {getExpiryDisplay(subscription.end_date).text}
            </span>
          </div>
          
          {subscription.status === 'expired' && (
            <span style={{
              color: 'var(--color-danger-hover)',
              fontWeight: '500'
            }}>
              Contact support to renew
            </span>
          )}
        </div>
      </Card.Content>
    </Card>
  );
};

export default SubscriptionStatus;